import { hashKey, useQuery } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';
import { useWorkspace } from '../../workspace';
import type { C3DataSource, C3Resource } from '../ports';
import { C3_QUERY_ROOT } from './clock';
import { useClockHandle, useDataSource } from './DataProvider';

export interface C3QueryOptions {
  /** False keeps the resource from being read at all, for a screen that shows something else (Demo). Default true. */
  enabled?: boolean;
}

export interface C3Result<T> {
  /** The records, or what they were when the pause began. Undefined until the first read succeeds. */
  data: T | undefined;
  /** The last read failed. `data` can still hold the read before it. */
  error: Error | null;
  /** Nothing has been read yet for this organization. */
  loading: boolean;
  /** A read is under way, the first or a later one. */
  fetching: boolean;
  /** The moment `data` stands for, in ms; 0 before the first read. */
  updatedAt: number;
}

/**
 * One resource of the port, cached by source, organization and name, and refreshed by the one clock.
 * A screen that reads the same resource as another shares its request.
 */
export function useC3Query<T>(
  resource: C3Resource,
  read: (source: C3DataSource) => Promise<T>,
  extraKey: readonly unknown[] = [],
  options: C3QueryOptions = {}
): C3Result<T> {
  const source = useDataSource();
  const clock = useClockHandle();
  const { org, loading: workspaceLoading } = useWorkspace();
  const queryKey = [C3_QUERY_ROOT, source.kind, org, resource, ...extraKey];
  const query = useQuery({
    queryKey,
    queryFn: () => read(source),
    // The API readers have nowhere to ask without an organization, so while the workspace is still loading the
    // screen keeps its loading state. Once it has finished with no organization (the API is down) the readers
    // ask anyway and fail, so the screen says it could not load instead of waiting for ever.
    // Demo needs none: the invented tenant answers whoever asks.
    enabled: (source.kind === 'fixture' || Boolean(org) || !workspaceLoading) && options.enabled !== false,
  });
  const hash = hashKey(queryKey);
  const frozen = useSyncExternalStore(clock.subscribe, () => clock.frozenFor(hash));

  return {
    data: frozen ? (frozen.data as T) : query.data,
    error: query.error,
    loading: !frozen && query.isPending,
    fetching: query.isFetching,
    updatedAt: frozen ? frozen.at : query.dataUpdatedAt,
  };
}

export const useThreatEvents = (o?: C3QueryOptions) =>
  useC3Query('threatEvents', (s) => s.listThreatEvents(), [], o);
export const useThreatCandidates = (o?: C3QueryOptions) =>
  useC3Query('threatCandidates', (s) => s.listThreatCandidates(), [], o);
export const useThreatElements = (o?: C3QueryOptions) =>
  useC3Query('threatElements', (s) => s.listThreatElements(), [], o);
export const useInfrastructureElements = (o?: C3QueryOptions) =>
  useC3Query('infrastructureElements', (s) => s.listInfrastructureElements(), [], o);
export const useBusinessElements = (o?: C3QueryOptions) =>
  useC3Query('businessElements', (s) => s.listBusinessElements(), [], o);
export const useSecurityEvents = (o?: C3QueryOptions) =>
  useC3Query('securityEvents', (s) => s.listSecurityEvents(), [], o);
export const useRemediations = (o?: C3QueryOptions) =>
  useC3Query('remediations', (s) => s.listRemediations(), [], o);
export const useBlastRadiusScores = (o?: C3QueryOptions) =>
  useC3Query('blastRadiusScores', (s) => s.listBlastRadiusScores(), [], o);
export const useEntityRiskScores = (o?: C3QueryOptions) =>
  useC3Query('entityRiskScores', (s) => s.listEntityRiskScores(), [], o);
export const useSources = (o?: C3QueryOptions) =>
  useC3Query('sources', (s) => s.listSources(), [], o);
